import { useCallback, useEffect, useRef, useState } from 'react';
import { useNonReactiveStore as useNonReactiveCalendarGridStore } from '../store/calendar-grid.store';
import { useNonReactiveZoomStore } from '../store/zoom.store';

type ScrollDirection = 'left' | 'right';
type ViewportSize = { height: number; width: number };

export const useScrollContainerRef = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [{ height: visibleHeight, width: visibleWidth }, setViewportSize] = useState<ViewportSize>({
    height: 0,
    width: 0,
  });

  const nonReactiveCalendarGridStore = useNonReactiveCalendarGridStore();
  const nonReactiveZoomStore = useNonReactiveZoomStore();

  const handleScroll = useCallback((direction: ScrollDirection) => {
    const element = containerRef.current;

    if (!element) return;

    const scrollAmount = Math.max(element.clientWidth * 0.8, 1);
    const left = direction === 'left' ? -scrollAmount : scrollAmount;

    element.scrollBy({ left, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    let scrollFrameId = 0;

    if (!element) return;

    const syncResolvedRowHeight = () => {
      const column = element.querySelector('[data-column-id]');
      const resolvedRowHeightPx = column ? parseFloat(getComputedStyle(column).gridTemplateRows) : undefined;
      if (resolvedRowHeightPx) {
        nonReactiveZoomStore.setState({ resolvedRowHeightPx });
      }
    };
    const syncViewportSize = () => {
      const nextViewportSize: ViewportSize = {
        height: element.clientHeight,
        width: element.clientWidth,
      };

      setViewportSize((currentViewportSize) =>
        currentViewportSize.width === nextViewportSize.width && currentViewportSize.height === nextViewportSize.height
          ? currentViewportSize
          : nextViewportSize
      );
    };

    const syncScrollState = () => {
      scrollFrameId = 0;

      const maxScrollLeft = Math.max(element.scrollWidth - element.clientWidth, 0);
      const canScrollLeft = element.scrollLeft > 0;
      const canScrollRight = maxScrollLeft - element.scrollLeft > 1;
      const { canScrollLeft: previousCanScrollLeft, canScrollRight: previousCanScrollRight } =
        nonReactiveCalendarGridStore.getState();

      if (previousCanScrollLeft === canScrollLeft && previousCanScrollRight === canScrollRight) {
        return;
      }

      nonReactiveCalendarGridStore.setState({ canScrollLeft, canScrollRight });
    };

    const scheduleSyncScrollState = () => {
      if (scrollFrameId !== 0) return;
      scrollFrameId = window.requestAnimationFrame(syncScrollState);
    };

    const handleResize = () => {
      syncViewportSize();
      syncResolvedRowHeight();
      scheduleSyncScrollState();
    };

    const resizeObserver = new ResizeObserver(handleResize);

    syncViewportSize();
    syncResolvedRowHeight();
    scheduleSyncScrollState();
    resizeObserver.observe(element);
    element.addEventListener('scroll', scheduleSyncScrollState, { passive: true });
    // zoom change updates the --row-height CSS var; wait a frame so layout reflects it before measuring
    const unsubscribeZoom = nonReactiveZoomStore.subscribe(
      (s) => s.pxPerMinute,
      () => window.requestAnimationFrame(syncResolvedRowHeight)
    );

    return () => {
      if (scrollFrameId !== 0) {
        window.cancelAnimationFrame(scrollFrameId);
      }

      resizeObserver.disconnect();
      element.removeEventListener('scroll', scheduleSyncScrollState);
      unsubscribeZoom();
    };
  }, [nonReactiveCalendarGridStore, nonReactiveZoomStore]);

  return {
    containerRef,
    handleScroll,
    visibleHeight,
    visibleWidth,
  };
};
